import React, { useEffect, useMemo, useState } from "react";
import {
  Layout,
  Table,
  Dropdown,
  Menu,
  Space,
  Input,
  Button,
  Pagination,
  message,
  Modal,
  Tooltip,
  Tag,
  Select,
  Skeleton,
} from "antd";
import {
  MoreOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import "./commanTable.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  setConfirmationData,
  setLimit,
  setPage,
  setSearchInput,
  setSearchItem,
  setSelectedRole,
  setSelectedRoleId,
  setSelectedUserRoleId,
  setSortConfig,
} from "../../redux/slices/globalSlice";
import { DialogBox } from "../dialog/DialogBox";
import TopHeader from "../header/Header";
import {
  isOrganization,
  isUserManagementEdit,
  isUserRoleEdit,
} from "../../utils/permissions";
import AppSecureStorage from "../../services/secureStorage";
import { Roles } from "../../utils/constants";
import {
  sendInvoiceForApproval,
  updateInvoiceStatus,
} from "../../redux/slices/invoicesSlice";
import { useNavigate } from "react-router-dom";
import { truncateWithTooltip } from "../../utils/helpers";
import debounce from "lodash.debounce";
const { Content, Footer } = Layout;
const storage = new AppSecureStorage();

const CommanTable = ({
  isTitleVisible = true,
  title,
  data,
  onDelete,
  visibleColumns,
  setVisibleColumns,
  apiCall,
  actionsOptions,
  icon,
  openAddPopup,
  renderPopupContent,
  isFooter,
  customFooter,
  onClose,
  isAdd,
  loading,
  titlePrefix,
  onChangeRole,
  rowSelection,
  openDeletePopup,
  showDeleteButton,
  showColumnDropdown = true,
  showOrganizationFilter = false,
  organizations = [],
  selectedOrganization,
  onOrganizationChange,
  orgLoading = false,
  isPopUpShow,
  width,
  customColumns,
  removeTableContentPadding = false,
  preserveSearch = false,
  scrollHeight = 495,
}) => {
  const dispatch = useDispatch();
  const role = storage.get("role");
  const navigate = useNavigate();
  const {
    page,
    limit,
    searchInput,
    totalPage,
    sortConfig,
    searchItemUserManagement,
  } = useSelector((state) => state.globalReducer);
  const [searchText, setSearchText] = useState(
    preserveSearch ? (searchInput ?? "") : "",
  );
  const [searchedColumn, setSearchedColumn] = useState(
    preserveSearch ? (searchItemUserManagement ?? "") : "",
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState("");
  const [deleteKeys, setDeleteKeys] = useState([]);
  const [previousRole, setPreviousRole] = useState("");
  const { getRolesData } = useSelector((state) => state.userManagementReducer);
  const showActionsForUserGroups =
    title === "User Groups" || title === "Broadcast Analytics" || title === "Scheduled Messages";
  const editPermissionEdit =
    showActionsForUserGroups ||
    (title === "User Role" && isUserRoleEdit) ||
    (title === "User" && isUserManagementEdit) ||
    (title === "Organization" && isOrganization === Roles.SUPER_ADMIN);

  const menu = (record, isProtectedRole) => {
    return (
      <Menu>
        {actionsOptions?.map((list, index) => {
          const isDisabled = isProtectedRole && list.label === "Edit";
          return (
            <Menu.Item
              key={`${list.label}-${index}`}
              onClick={() => {
                list?.acttion(record);
              }}
              disabled={isDisabled}
              icon={list?.icon}
              style={{
                color: isDisabled
                  ? "var(--secondary-color)"
                  : "var(--text-color)",
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
            >
              {list?.label}
            </Menu.Item>
          );
        })}
      </Menu>
    );
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        dispatch(setPage(1));
        dispatch(setSearchInput(value));
        apiCall(1, limit, value);
      }, 600),
    [limit],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const getColumnSearchProps = (dataIndex) => ({
    ...(preserveSearch && searchInput && searchItemUserManagement === dataIndex
      ? { defaultFilteredValue: [String(searchInput)] }
      : {}),
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search by ${
            dataIndex == "whatsappContactNumber" ? "number" : dataIndex
          }`}
          value={selectedKeys[0] || ""}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            confirm({ closeDropdown: false });
            dispatch(setSearchItem(dataIndex));
            debouncedSearch(e.target.value);
          }}
          onPressEnter={() => {
            debouncedSearch.cancel();
            confirm();
            dispatch(setPage(1));
            dispatch(setSearchItem(dataIndex));
            dispatch(setSearchInput(selectedKeys[0] || ""));
            apiCall(1, limit, selectedKeys[0] || "");
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              dispatch(setPage(1));
              dispatch(setSearchItem(dataIndex));
              dispatch(setSearchInput(selectedKeys[0] || ""));
              apiCall(1, limit, selectedKeys[0] || "");
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 120, height: 44 }}
            className="search-input"
          >
            Search
          </Button>
          <Button
            size="small"
            style={{ width: 120, height: 44 }}
            onClick={() => {
              debouncedSearch.cancel();
              clearFilters();
              confirm();
              dispatch(setSearchInput(""));
              dispatch(setSearchItem(""));
              dispatch(setPage(1));
              dispatch(setLimit(10));
              apiCall(1, 10, "");
            }}
            className="reset-btn"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{ color: filtered ? "var(--primary-color)" : undefined }}
      />
    ),
    onFilter: (value, record) => {
      const fieldValue = record[dataIndex];
      return fieldValue && typeof fieldValue !== "undefined"
        ? fieldValue.toString().toLowerCase().includes(value.toLowerCase())
        : false;
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleDelete = (keys) => {
    onDelete(keys);
    setSelectedRowKeys([]);
    message.success("Deleted successfully");
  };

  const showDeleteConfirm = (keys, text) => {
    setDeleteKeys(keys);
    setModalText(text);
    setIsModalVisible(true);
  };

  const handleModalConfirm = () => {
    handleDelete(deleteKeys);
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  // Parent checkbox state
  const allChecked = Object.entries(visibleColumns || {})
    .filter(([columnKey]) => columnKey !== "sNo")
    .every(([, isVisible]) => Boolean(isVisible));

  // Handler for parent (all) checkbox
  const handleAllToggle = () => {
    const togglableColumns = Object.keys(visibleColumns || {}).filter(
      (key) => key !== "sNo",
    );
    const allTogglableVisible = togglableColumns.every(
      (key) => visibleColumns[key],
    );
    const newValue = !allTogglableVisible;
    const PROTECTED_COLUMNS = ["name", "groupName"]; // Critical fields to always keep visible

    setVisibleColumns((prev) => {
      const updated = {};
      Object.keys(prev).forEach((key) => {
        if (key === "sNo") {
          updated[key] = prev[key];
          return;
        }
        // Keep protected columns visible, toggle all others
        updated[key] = PROTECTED_COLUMNS.includes(key) ? true : newValue;
      });
      return updated;
    });
  };

  // Handler for child (individual column) checkbox
  const handleColumnToggle = (columnKey) => {
    if (columnKey === "sNo") return;

    const PROTECTED_COLUMNS = ["name", "groupName"];
    // Prevent deselecting protected columns
    if (PROTECTED_COLUMNS.includes(columnKey) && visibleColumns[columnKey]) {
      message.warning(
        `${columnKey === "name" ? "Name" : "Group Name"} field is required and cannot be hidden`,
      );
      return;
    }
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleRoleChange = (id, value, currentRole) => {
    const selectedRoleObj = getRolesData?.data?.find(
      (role) => role.name === value,
    );
    const roleId = selectedRoleObj._id;
    setPreviousRole(currentRole);
    dispatch(setSelectedUserRoleId(id));
    dispatch(setSelectedRole(value));
    dispatch(setConfirmationData(value));
    dispatch(setSelectedRoleId(roleId));
    onChangeRole();
  };

  const roleOptions = getRolesData?.data?.map((role) => ({
    label: role.name,
    value: role.name,
  }));

  const defaultColumns = [
    ...(title === "Invoices"
      ? [
          {
            title: "Month",
            align: "left",
            dataIndex: "monthYear",
            onHeaderCell: () => ({
              onClick: () => {
                const newSortDirection =
                  sortConfig.direction === "asc" ? "desc" : "asc";
                dispatch(
                  setSortConfig({
                    key: "monthYear",
                    direction: newSortDirection,
                  }),
                );
                apiCall(
                  page,
                  limit,
                  searchInput,
                  "monthYear",
                  newSortDirection,
                );
              },
            }),
          },
          {
            title: "Active Users",
            dataIndex: "activeUsers",
            align: "left",
          },
          {
            title: "Rate/User",
            dataIndex: "ratePerUser",
            align: "left",
          },
          {
            title: "Total Amount",
            dataIndex: "totalAmount",
            align: "left",
          },
          {
            title: "Status",
            dataIndex: "status",
          },
          ...(role !== Roles.SUPER_ADMIN
            ? [
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, record) => (
                    <Dropdown
                      overlay={
                        <Menu>
                          {/* <Menu.Item 
                        key="download" 
                        icon={<DownloadOutlined />}
                        onClick={() => window.open(record.invoicePdf, '_blank')}
                      >
                        Download Invoice
                      </Menu.Item> */}

                          <Menu.Item
                            key="approve"
                            disabled={["Discarded", "Draft", "Paid"].includes(
                              record.status,
                            )}
                            onClick={() => {
                              dispatch(
                                sendInvoiceForApproval({
                                  invoiceId: record.key,
                                  navigate,
                                }),
                              );
                            }}
                          >
                            Send for Approval
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={["click"]}
                      placement="bottomLeft"
                    >
                      <a
                        className="ant-dropdown-link"
                        onClick={(e) => e.preventDefault()}
                      >
                        <Space>
                          <MoreOutlined style={{ fontSize: "18px" }} />
                        </Space>
                      </a>
                    </Dropdown>
                  ),
                },
              ]
            : []),
        ]
      : title === "Invoice Approvals"
        ? [
            {
              title: "Invoice #",
              dataIndex: "invoiceNumber",
              key: "invoiceNumber",
              align: "left",
            },
            {
              title: "Month",
              dataIndex: "monthYear",
              key: "month",
              align: "left",
            },
            {
              title: "Total Amount",
              dataIndex: "totalAmount",
              key: "totalAmount",
              align: "left",
            },
            {
              title: "Amount Paid",
              dataIndex: "amountPaid",
              key: "amountPaid",
              align: "left",
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (_, record) => {
                const statusOptions = [
                  "DISCARDED",
                  "UNPAID",
                  "PARTIALLY_PAID",
                  "PAID",
                  "OVERDUE",
                ];
                const statusLabels = {
                  DISCARDED: "Discarded",
                  UNPAID: "Unpaid",
                  PARTIALLY_PAID: "Partially Paid",
                  PAID: "Paid",
                  OVERDUE: "Overdue",
                };
                return (
                  <Select
                    defaultValue={record.rawData.invoiceId?.status}
                    style={{ width: 150 }}
                    onChange={(value) => {
                      dispatch(
                        updateInvoiceStatus({
                          invoiceId: record.rawData._id,
                          status: value,
                          navigate,
                        }),
                      );
                    }}
                  >
                    {statusOptions.map((option) => (
                      <Select.Option key={option} value={option}>
                        {statusLabels[option]}
                      </Select.Option>
                    ))}
                  </Select>
                );
              },
            },
          ]
        : title === "User Groups"
          ? [
              {
                title: "Group Name",
                dataIndex: "groupName",
                ...(visibleColumns.groupName
                  ? {
                      sorter: true,
                      onHeaderCell: () => ({
                        onClick: () => {
                          const newSortDirection =
                            sortConfig.direction === "asc" ? "desc" : "asc";
                          dispatch(
                            setSortConfig({
                              key: "name",
                              direction: newSortDirection,
                            }),
                          );
                          apiCall(
                            page,
                            limit,
                            searchInput,
                            "name",
                            newSortDirection,
                          );
                        },
                      }),
                      ...getColumnSearchProps("groupName"),
                      render: (text) => truncateWithTooltip(text, 20),
                    }
                  : { render: () => null, className: "hidden-column" }),
              },
              {
                title: "User Count",
                dataIndex: "userCount",
                ...(visibleColumns.userCount
                  ? {
                      sorter: false,
                    }
                  : { render: () => null, className: "hidden-column" }),
              },
              {
                title: "Created On",
                dataIndex: "createdOn",
                ...(visibleColumns.createdOn
                  ? {
                      sorter: true,
                      onHeaderCell: () => ({
                        onClick: () => {
                          const newSortDirection =
                            sortConfig.direction === "asc" ? "desc" : "asc";
                          dispatch(
                            setSortConfig({
                              key: "createdAt",
                              direction: newSortDirection,
                            }),
                          );
                          apiCall(
                            page,
                            limit,
                            searchInput,
                            "createdAt",
                            newSortDirection,
                          );
                        },
                      }),
                    }
                  : { render: () => null, className: "hidden-column" }),
              },
            ]
          : title === "Broadcast Analytics"
            ? [
                {
                  title: "Template Name",
                  dataIndex: "message",
                  key: "message",
                },
                {
                  title: "Broadcasted On",
                  dataIndex: "createdAt",
                  key: "createdAt",
                },
                {
                  title: "Broadcasted Users",
                  dataIndex: "broadcastedUsers",
                  key: "broadcastedUsers",
                  align: "center",
                },
              ]
            : title === "Template"
              ? [
                  {
                    title: "Name",
                    dataIndex: "name",
                    key: "name",
                    ...getColumnSearchProps("name"),
                  },

                  {
                    title: "Message",
                    dataIndex: "bodyText",
                    key: "bodyText",
                    render: (text) => truncateWithTooltip(text, 30),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    render: (text) => {
                      const normalized = (text || "")
                        .toString()
                        .trim()
                        .toLowerCase();

                      const bgColor = {
                        approved: "#b7eb8f",
                        rejected: "#ffa39e",
                      };

                      const statusColor = {
                        approved: "#389e0d",
                        rejected: "#cf1322",
                      };

                      return (
                        <div
                          style={{
                            backgroundColor: bgColor[normalized] || "#f1c21b",
                            display: "inline-block",
                            fontSize: "0.7rem",
                            borderRadius: "6px",
                            padding: "4px 8px",
                          }}
                        >
                          <span
                            style={{
                              color: statusColor[normalized] || "black",
                            }}
                          >
                            {text}
                          </span>
                        </div>
                      );
                    },
                  },
                  {
                    title: "Category",
                    dataIndex: "category",
                    key: "category",
                  },
                ]
              : title === "User Role"
                ? [
                    {
                      title: "Name",
                      dataIndex: "name",
                      ...(visibleColumns.name
                        ? {
                            sorter: true,
                            onHeaderCell: () => ({
                              onClick: () => {
                                const newSortDirection =
                                  sortConfig.direction === "asc"
                                    ? "desc"
                                    : "asc";
                                dispatch(
                                  setSortConfig({
                                    key: "name",
                                    direction: newSortDirection,
                                  }),
                                );

                                apiCall(
                                  page,
                                  limit,
                                  searchInput,
                                  "name",
                                  newSortDirection,
                                );
                              },
                            }),
                            ...getColumnSearchProps("name"),
                          }
                        : { render: () => null, className: "hidden-column" }),
                    },
                    {
                      title: "User Count",
                      dataIndex: "userCount",
                      ...(visibleColumns.userCount
                        ? {
                            sorter: false,
                          }
                        : { render: () => null, className: "hidden-column" }),
                    },
                    {
                      title: "Permissions",
                      dataIndex: "permissions",
                      width: 150,
                      render: (permissions) => (
                        <div className="permissions-container">
                          {Object.keys(permissions).map((category) => (
                            <Tooltip key={category} title={category}>
                              <Tag
                                key={category}
                                style={{ margin: "2px" }}
                                ellipsis
                              >
                                {category}
                              </Tag>
                            </Tooltip>
                          ))}
                        </div>
                      ),
                      sorter: true,
                      onHeaderCell: () => ({
                        onClick: () => {
                          const newSortDirection =
                            sortConfig.direction === "asc" ? "desc" : "asc";
                          dispatch(
                            setSortConfig({
                              key: "permissions",
                              direction: newSortDirection,
                            }),
                          );
                          apiCall(
                            page,
                            limit,
                            searchInput,
                            "permissions",
                            newSortDirection,
                          );
                        },
                      }),
                    },
                  ]
                : title === "User"
                  ? [
                      {
                        title: "Name",
                        dataIndex: "name",
                        ...(visibleColumns.name
                          ? {
                              sorter: true,
                              onHeaderCell: () => ({
                                onClick: () => {
                                  const newSortDirection =
                                    sortConfig.direction === "asc"
                                      ? "desc"
                                      : "asc";
                                  dispatch(
                                    setSortConfig({
                                      key: "name",
                                      direction: newSortDirection,
                                    }),
                                  );
                                  apiCall(
                                    page,
                                    limit,
                                    searchInput,
                                    sortConfig.key,
                                    newSortDirection,
                                  );
                                },
                              }),
                              ...getColumnSearchProps("name"),
                            }
                          : { render: () => null, className: "hidden-column" }),
                      },
                      {
                        title: (
                          <div style={{ textAlign: "center" }}>Number</div>
                        ),
                        dataIndex: "whatsappContactNumber",
                        ...(visibleColumns.number
                          ? {
                              render: (text) => (
                                <div style={{ textAlign: "center" }}>
                                  {text ? text : "-"}
                                </div>
                              ),
                            }
                          : { render: () => null, className: "hidden-column" }),
                      },
                      {
                        title: "Email",
                        dataIndex: "email",
                        ...(visibleColumns.email
                          ? {
                              sorter: true,
                              onHeaderCell: () => ({
                                onClick: () => {
                                  const newSortDirection =
                                    sortConfig.direction === "asc"
                                      ? "desc"
                                      : "asc";
                                  dispatch(
                                    setSortConfig({
                                      key: "email",
                                      direction: newSortDirection,
                                    }),
                                  );
                                  apiCall(
                                    page,
                                    limit,
                                    searchInput,
                                    sortConfig.key,
                                    newSortDirection,
                                  );
                                },
                              }),
                              ...getColumnSearchProps("email"),
                              render: (text) => decodeURIComponent(text),
                            }
                          : { render: () => null, className: "hidden-column" }),
                      },
                      {
                        title: "Role",
                        dataIndex: "role",
                        sorter: true,
                        render: (text, record) => (
                          <Select
                            value={text}
                            style={{ width: 120 }}
                            onChange={(value) =>
                              handleRoleChange(record._id, value, text)
                            }
                          >
                            {roleOptions?.map((option) => (
                              <Select.Option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </Select.Option>
                            ))}
                          </Select>
                        ),
                        className: "role-column",
                        onHeaderCell: () => ({
                          onClick: () => {
                            const newSortDirection =
                              sortConfig.direction === "asc" ? "desc" : "asc";
                            dispatch(
                              setSortConfig({
                                key: "role",
                                direction: newSortDirection,
                              }),
                            );
                            apiCall(
                              page,
                              limit,
                              searchInput,
                              "role",
                              newSortDirection,
                            );
                          },
                        }),
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        ...(visibleColumns.status
                          ? {
                              sorter: true,
                              onHeaderCell: () => ({
                                onClick: () => {
                                  const newSortDirection =
                                    sortConfig.direction === "asc"
                                      ? "desc"
                                      : "asc";
                                  dispatch(
                                    setSortConfig({
                                      key: "verified",
                                      direction: newSortDirection,
                                    }),
                                  );
                                  apiCall(
                                    page,
                                    limit,
                                    searchInput,
                                    "verified",
                                    newSortDirection,
                                  );
                                },
                              }),
                              render: (text, record) => (
                                <Tag color={record.verified ? "green" : "red"}>
                                  {record.verified ? "Verified" : "Unverified"}
                                </Tag>
                              ),
                              className: "status-column",
                            }
                          : { render: () => null, className: "hidden-column" }),
                      },
                    ]
                  : title === "sendMessageUsers"
                    ? [
                        {
                          title: "Name",
                          dataIndex: "name",
                          ...(visibleColumns.name
                            ? {
                                sorter: true,
                                onHeaderCell: () => ({
                                  onClick: () => {
                                    const newSortDirection =
                                      sortConfig.direction === "asc"
                                        ? "desc"
                                        : "asc";
                                    dispatch(
                                      setSortConfig({
                                        key: "name",
                                        direction: newSortDirection,
                                      }),
                                    );
                                    apiCall(
                                      page,
                                      limit,
                                      searchInput,
                                      sortConfig.key,
                                      newSortDirection,
                                    );
                                  },
                                }),
                                ...getColumnSearchProps("name"),
                              }
                            : {
                                render: () => null,
                                className: "hidden-column",
                              }),
                        },
                        {
                          title: (
                            <div style={{ textAlign: "center" }}>Number</div>
                          ),
                          dataIndex: "whatsappContactNumber",
                          ...(visibleColumns.number
                            ? {
                                render: (text) => (
                                  <div style={{ textAlign: "center" }}>
                                    {text ? text : "-"}
                                  </div>
                                ),
                              }
                            : {
                                render: () => null,
                                className: "hidden-column",
                              }),
                        },
                        {
                          title: "Email",
                          dataIndex: "email",
                          ...(visibleColumns.email
                            ? {
                                sorter: true,
                                onHeaderCell: () => ({
                                  onClick: () => {
                                    const newSortDirection =
                                      sortConfig.direction === "asc"
                                        ? "desc"
                                        : "asc";
                                    dispatch(
                                      setSortConfig({
                                        key: "email",
                                        direction: newSortDirection,
                                      }),
                                    );
                                    apiCall(
                                      page,
                                      limit,
                                      searchInput,
                                      sortConfig.key,
                                      newSortDirection,
                                    );
                                  },
                                }),
                                ...getColumnSearchProps("email"),
                                render: (text) => decodeURIComponent(text),
                              }
                            : {
                                render: () => null,
                                className: "hidden-column",
                              }),
                        },
                        {
                          title: "Role",
                          dataIndex: "role",
                          sorter: true,
                          className: "role-column",
                          onHeaderCell: () => ({
                            onClick: () => {
                              const newSortDirection =
                                sortConfig.direction === "asc" ? "desc" : "asc";
                              dispatch(
                                setSortConfig({
                                  key: "role",
                                  direction: newSortDirection,
                                }),
                              );
                              apiCall(
                                page,
                                limit,
                                searchInput,
                                "role",
                                newSortDirection,
                              );
                            },
                          }),
                        },
                        {
                          title: "Status",
                          dataIndex: "status",
                          ...(visibleColumns.status
                            ? {
                                sorter: true,
                                onHeaderCell: () => ({
                                  onClick: () => {
                                    const newSortDirection =
                                      sortConfig.direction === "asc"
                                        ? "desc"
                                        : "asc";
                                    dispatch(
                                      setSortConfig({
                                        key: "verified",
                                        direction: newSortDirection,
                                      }),
                                    );
                                    apiCall(
                                      page,
                                      limit,
                                      searchInput,
                                      "verified",
                                      newSortDirection,
                                    );
                                  },
                                }),
                                render: (text, record) => (
                                  <Tag
                                    color={record.verified ? "green" : "red"}
                                  >
                                    {record.verified
                                      ? "Verified"
                                      : "Unverified"}
                                  </Tag>
                                ),
                                className: "status-column",
                              }
                            : {
                                render: () => null,
                                className: "hidden-column",
                              }),
                        },
                      ]
                    : title === "sendMessageGroups"
                      ? [
                          {
                            title: "Group Name",
                            dataIndex: "groupName",
                            ...(visibleColumns.groupName
                              ? {
                                  sorter: true,
                                  onHeaderCell: () => ({
                                    onClick: () => {
                                      const newSortDirection =
                                        sortConfig.direction === "asc"
                                          ? "desc"
                                          : "asc";
                                      dispatch(
                                        setSortConfig({
                                          key: "name",
                                          direction: newSortDirection,
                                        }),
                                      );
                                      apiCall(
                                        page,
                                        limit,
                                        searchInput,
                                        "name",
                                        newSortDirection,
                                      );
                                    },
                                  }),
                                  ...getColumnSearchProps("groupName"),
                                  render: (text) =>
                                    truncateWithTooltip(text, 20),
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                          {
                            title: "User Count",
                            dataIndex: "userCount",
                            ...(visibleColumns.userCount
                              ? {
                                  sorter: false,
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                          {
                            title: "Created On",
                            dataIndex: "createdOn",
                            ...(visibleColumns.createdOn
                              ? {
                                  sorter: true,
                                  onHeaderCell: () => ({
                                    onClick: () => {
                                      const newSortDirection =
                                        sortConfig.direction === "asc"
                                          ? "desc"
                                          : "asc";
                                      dispatch(
                                        setSortConfig({
                                          key: "createdAt",
                                          direction: newSortDirection,
                                        }),
                                      );
                                      apiCall(
                                        page,
                                        limit,
                                        searchInput,
                                        "createdAt",
                                        newSortDirection,
                                      );
                                    },
                                  }),
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                        ]
                      : [
                          {
                            title: "Name",
                            dataIndex: "name",
                            ...(visibleColumns.name
                              ? {
                                  sorter: (a, b) =>
                                    (a.name || "").localeCompare(b.name || ""),
                                  ...getColumnSearchProps("name"),
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                          {
                            title: "Domains",
                            dataIndex: "domains",
                            ...(visibleColumns.domain
                              ? {
                                  sorter: (a, b) => {
                                    const domainA =
                                      a?.domains?.length > 0
                                        ? a.domains[0]
                                        : a?.domain || "";

                                    const domainB =
                                      b?.domains?.length > 0
                                        ? b.domains[0]
                                        : b?.domain || "";
                                    return domainA.localeCompare(domainB);
                                  },

                                  ...getColumnSearchProps("domain"),

                                  render: (_, record) => {
                                    const domains =
                                      record?.domains?.length > 0
                                        ? record.domains
                                        : record?.domain
                                          ? [record.domain]
                                          : [];

                                    if (!domains.length) {
                                      return "-";
                                    }

                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "4px",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {domains.map((domain, index) => (
                                          <Tag
                                            key={`${domain}-${index}`}
                                            style={{
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {domain}
                                          </Tag>
                                        ))}
                                      </div>
                                    );
                                  },
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                          {
                            title: "Company Email",
                            dataIndex: "admin",
                            ...(visibleColumns.companyEmail
                              ? {
                                  sorter: (a, b) =>
                                    (a.companyEmail || "").localeCompare(
                                      b.companyEmail || "",
                                    ),
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            ...(visibleColumns.status
                              ? {
                                  sorter: (a, b) =>
                                    (a.status || "").localeCompare(
                                      b.status || "",
                                    ),
                                  render: (status) => (
                                    <Tag
                                      color={
                                        status === "active" ? "green" : "red"
                                      }
                                    >
                                      {status}
                                    </Tag>
                                  ),
                                }
                              : {
                                  render: () => null,
                                  className: "hidden-column",
                                }),
                          },
                        ]),
    ...(editPermissionEdit &&
    (typeof actionsOptions !== "function" ||
      data.some((record) => {
        const options = actionsOptions(record);
        return options && options.length > 0;
      }))
      ? [
          {
            title:
              selectedRowKeys?.length > 0 ? (
                <div
                  onClick={() => {
                    const hasProtectedRoles = data.some(
                      (record) =>
                        selectedRowKeys.includes(record.key) &&
                        ["admin", "employee"].includes(record.name),
                    );

                    if (!hasProtectedRoles) {
                      showDeleteConfirm(
                        selectedRowKeys,
                        "Are you sure you want to delete all data?",
                      );
                    }
                  }}
                >
                  <DeleteOutlined style={{ marginRight: "10px" }} />
                  Delete Data
                </div>
              ) : (
                "Actions"
              ),
            key: "actions",
            render: (text, record) => {
              const options =
                typeof actionsOptions === "function"
                  ? actionsOptions(record)
                  : actionsOptions;

              const isProtectedRole = ["admin", "employee"].includes(
                record?.name,
              );

              const singleDeleteOption = options?.length === 1;

              if (singleDeleteOption) {
                return (
                  <Button
                    type="link"
                    disabled={isProtectedRole}
                    onClick={() => options[0].acttion(record)}
                    style={{
                      padding: 0,
                      color: isProtectedRole
                        ? "var(--secondary-color)"
                        : "var(--text-color)",
                      opacity: isProtectedRole ? 0.5 : 1,
                      cursor: isProtectedRole ? "not-allowed" : "pointer",
                    }}
                  >
                    {options[0].icon}
                    {options[0].label}
                  </Button>
                );
              }

              return (
                <Dropdown
                  overlay={menu(record, isProtectedRole)}
                  trigger={["click"]}
                  // disabled={isProtectedRole}
                  placement="bottomRight"
                >
                  <a
                    className="common-table-actions"
                    onClick={(e) => e.preventDefault()}
                    style={
                      {
                        // cursor: isProtectedRole ? "not-allowed" : "pointer",
                        // opacity: isProtectedRole ? 0.5 : 1,
                      }
                    }
                  >
                    <Space>
                      <MoreOutlined
                        style={{
                          // color: isProtectedRole ? "#454545" : "#141a40",
                          color: "var(--text-color)",
                          fontSize: "20px",
                        }}
                      />
                    </Space>
                  </a>
                </Dropdown>
              );
            },
          },
        ]
      : []),
  ];
  const columns = (customColumns?.length ? customColumns : defaultColumns).filter(
      (col) => visibleColumns[col.dataIndex] !== false,
    );
  useEffect(() => {
    apiCall(1, limit, preserveSearch ? (searchInput ?? "") : "");
  }, []);

  return (
    <Layout className="table-layout">
      {isTitleVisible && (
        <TopHeader
          isTitle={true}
          isRightContent={true}
          title={title}
          dropdownItems={visibleColumns}
          icon={icon}
          onChangeOptions={handleColumnToggle}
          onChangeAllOptions={handleAllToggle}
          allChecked={allChecked}
          isAdd={isAdd ? isAdd : false}
          openAddPopup={openAddPopup}
          titlePrefix={titlePrefix}
          openDeletePopup={openDeletePopup}
          showDeleteButton={showDeleteButton}
          showColumnDropdown={showColumnDropdown}
        />
      )}
      {showOrganizationFilter && (
        <div style={{ marginTop: "1rem", marginBottom: 16 }}>
          <h4 className="theme-text" style={{ marginBottom: 8 }}>
            Select Organization
          </h4>
          <Select
            style={{ width: 300 }}
            placeholder="Select Organization"
            value={selectedOrganization}
            onChange={onOrganizationChange}
            loading={orgLoading}
            allowClear
          >
            {organizations?.map((org) => (
              <Select.Option key={org._id} value={org._id}>
                {org.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}
      <Content style={{ padding: removeTableContentPadding ? "0px" : "24px" }}>
        <Table
          rowKey={(record) => record._id || record.id || record.key}
          scroll={{
            y: scrollHeight,
            x: "max-content",
          }}
          rowSelection={
            visibleColumns?.sNo !== false ? (rowSelection ?? null) : null
          }
          columns={columns}
          dataSource={data}
          pagination={false}
          rowClassName={(record) =>
            record.permissions === "Admin" || record.role === "Admin"
              ? "disabled-row"
              : ""
          }
          showSorterTooltip={{ target: "sorter-icon" }}
          loading={loading}
          locale={{
            emptyText: loading || data === undefined ? <Skeleton /> : undefined,
          }}
        />
      </Content>
      <Footer className="pagination-footer">
        <Pagination
          current={page}
          pageSize={limit}
          total={totalPage}
          showSizeChanger={true}
          onChange={(page, pageSize) => {
            dispatch(setPage(page));
            dispatch(setLimit(pageSize));
            apiCall(
              page,
              pageSize,
              searchInput,
              sortConfig?.key,
              sortConfig?.direction,
            );
          }}
          style={{ marginTop: "16px", textAlign: "right" }}
        />
      </Footer>
      <Modal
        title="Confirm Deletion"
        open={isModalVisible}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}
      >
        <p>{modalText}</p>
      </Modal>
      {title !== "sendMessageUsers" && (
        <DialogBox
          renderUi={renderPopupContent}
          isFooter={isFooter}
          customFooter={customFooter}
          onClose={onClose}
          isPopUpShow={isPopUpShow}
          width={width}
        />
      )}
    </Layout>
  );
};

export default CommanTable;
